/**
 * ClaimBanner - Shows claim period status and claim action for holders
 *
 * States:
 * - No active period: Don't render
 * - Active period, not logged in: "Log in to claim"
 * - Active period, needs reward address: "Set your address in Settings"
 * - Active period, eligible: "Claim Rewards" button
 * - Active period, already claimed: Checkmark with confirmation
 * - Closed period, user claimed: Show snapshotted points and payout
 *
 * Feature: 017-rewards-claim-system
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../../ui/components/Button';
import { Badge } from '../../ui/components/Badge';
import { FeatherCheckCircle, FeatherGift, FeatherAlertCircle } from '@subframe/core';
import { useAuthStore } from '../../store/authStore';
import { useActivePeriod, useSubmitClaim } from '../../hooks/useRewards';
import { getRewardAddresses } from '../../api/settings';
import type { ClaimPeriod, RewardClaim, EligibleCollection } from '../../api/rewards';

interface ClaimBannerProps {
  projectSlug: string;
}

export const ClaimBanner: React.FC<ClaimBannerProps> = ({
  projectSlug,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuthStore();
  const { data, isLoading } = useActivePeriod(projectSlug);
  const submitClaimMutation = useSubmitClaim(projectSlug);

  // Fetch reward addresses directly (shares React Query cache with Settings page)
  const { data: rewardAddressData } = useQuery({
    queryKey: ['reward-addresses'],
    queryFn: getRewardAddresses,
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
  const userRewardAddresses = rewardAddressData?.reward_addresses ?? {};

  if (isLoading || !data?.period) {
    return null;
  }

  const { period, userClaims, eligible_collections } = data;

  const handleClaim = () => {
    submitClaimMutation.mutate();
  };

  // Check if user has the required reward address
  const hasRequiredAddress =
    period.required_reward_address_type === 'xrp' ||
    !!userRewardAddresses[period.required_reward_address_type];

  return (
    <div className="w-full rounded-lg border border-solid border-green-600/30 bg-green-600/10 p-4">
      <div className="flex w-full flex-col gap-3">
        {/* Header */}
        <div className="flex items-center gap-2">
          <FeatherGift className="h-5 w-5 text-green-500" />
          <span className="text-body-bold font-body-bold text-default-font">
            Rewards Claim Period
          </span>
          <Badge variant={period.status === 'open' ? 'success' : 'neutral'}>
            {period.status === 'open' ? 'Open' : period.status === 'closed' ? 'Closed' : 'Distributed'}
          </Badge>
        </div>

        {/* Custom message */}
        {period.custom_message && (
          <p className="text-body font-body text-subtext-color">
            Message from Project: &ldquo;{period.custom_message}&rdquo;
          </p>
        )}

        {/* Rate info */}
        {period.rate_per_point && (
          <p className="text-caption font-caption text-subtext-color">
            Rate: {period.rate_per_point} per point
            {period.rate_mode === 'fixed_pool' && period.total_pool
              ? ` (${period.total_pool.toLocaleString()} total pool)`
              : ''}
          </p>
        )}

        {/* Eligible collections */}
        {eligible_collections && eligible_collections.length > 0 && (
          <p className="text-caption font-caption text-subtext-color">
            Eligible: {eligible_collections.map(c => c.name).join(', ')}
          </p>
        )}

        {/* Action area */}
        {!isAuthenticated ? (
          <div className="flex items-center justify-end">
            <Button
              variant="brand-primary"
              size="medium"
              onClick={() => navigate('/login')}
            >
              Log in to claim rewards
            </Button>
          </div>
        ) : period.status === 'open' && userClaims.length === 0 ? (
          <>
            {!hasRequiredAddress ? (
              <div className="flex items-center gap-2">
                <FeatherAlertCircle className="h-4 w-4 text-yellow-500" />
                <span className="text-caption font-caption text-default-font">
                  Set your {period.required_reward_address_type === 'coreum' ? 'Coreum' : 'XRP'} address in{' '}
                  <button
                    className="underline font-bold"
                    onClick={() => navigate('/settings')}
                  >
                    Settings
                  </button>{' '}
                  to claim rewards
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-caption font-caption text-subtext-color">
                  Your claim will include all NFTs across your linked wallets. Points will be snapshotted when the period closes.
                </p>
                <Button
                  variant="brand-primary"
                  size="medium"
                  className="flex-none"
                  disabled={submitClaimMutation.isPending}
                  onClick={handleClaim}
                >
                  {submitClaimMutation.isPending ? 'Claiming...' : 'Claim Rewards'}
                </Button>
              </div>
            )}
          </>
        ) : userClaims.length > 0 ? (
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <FeatherCheckCircle className="h-4 w-4 text-green-500" />
              <span className="text-body font-body text-default-font">
                Claimed with {userClaims.length} wallet{userClaims.length !== 1 ? 's' : ''}
              </span>
              {userClaims.some(c => c.snapshotted_points !== null) && (
                <span className="text-caption font-caption text-subtext-color ml-2">
                  ({userClaims.reduce((sum, c) => sum + (c.snapshotted_points ?? 0), 0).toLocaleString()} points
                  {userClaims.some(c => c.calculated_payout !== null)
                    ? `, payout: ${userClaims.reduce((sum, c) => sum + (c.calculated_payout ?? 0), 0).toLocaleString()}`
                    : ''
                  })
                </span>
              )}
            </div>
            {period.status === 'open' && (
              <p className="text-caption font-caption text-subtext-color">
                Points will be snapshotted and payouts calculated when the claim period closes.
              </p>
            )}
          </div>
        ) : null}

        {/* Error */}
        {submitClaimMutation.error && (
          <p className="text-caption font-caption text-red-600">
            {submitClaimMutation.error.message}
          </p>
        )}
      </div>
    </div>
  );
};
